import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Clock, Send, MessageSquare, Building2 } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { generateBreadcrumbSchema, SEO_CONFIG } from '../utils/seo';

const ContactPage = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  
  // Breadcrumb schema for search results
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Contact' }
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success(t('contact.success_message') || 'Your message has been sent successfully!');
        setFormData({
          name: '',
          email: '',
          company: '',
          subject: '',
          message: ''
        });
      } else {
        toast.error(data.detail || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error('Failed to send message. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="contact-page">
      <SEOHead 
        title="Contact Us | TradeHub24 Virtual Tradeshow Platform"
        description="Get in touch with TradeHub24. Contact our team for questions about virtual tradeshows, exhibitor registration, or partnership opportunities."
        canonicalUrl={`${SEO_CONFIG.siteUrl}/contact`}
        structuredData={breadcrumbSchema}
      />
      
      {/* Hero Section */}
      <section className="bg-primary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <MessageSquare className="h-16 w-16 mx-auto mb-6" />
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">{t('contact.title')}</h1>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto">
              {t('contact.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Information & Form */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-slate-900 mb-6">{t('contact.get_in_touch')}</h2>
                <p className="text-slate-600 mb-8">
                  {t('contact.get_in_touch_text')}
                </p>
              </div>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-primary/10 rounded-full p-3">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{t('contact.email')}</h3>
                    <p className="text-slate-600">support@tradehub24.com</p>
                    <p className="text-slate-600">sales@tradehub24.com</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-primary/10 rounded-full p-3">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{t('contact.phone')}</h3>
                    <p className="text-slate-600">+1 (980) 613-4193 (USA)</p>
                    <p className="text-slate-600">pending (Europe)</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-primary/10 rounded-full p-3">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{t('contact.office')}</h3>
                    <p className="text-slate-600"> 10926 David Taylor Drive </p>
                    <p className="text-slate-600">Suite 120 </p>
                    <p className="text-slate-600">Charlotte, NC 28262 USA</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-primary/10 rounded-full p-3">
                    <Clock className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{t('contact.business_hours')}</h3>
                    <p className="text-slate-600">{t('contact.hours_weekday')}</p>
                    <p className="text-slate-600">{t('contact.hours_weekend')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg p-8 shadow-sm border border-slate-200">
                <div className="flex items-center mb-6">
                  <Building2 className="h-8 w-8 text-primary mr-3" />
                  <h2 className="text-2xl font-bold text-slate-900">{t('contact.send_message')}</h2>
                </div>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">{t('contact.your_name')} *</Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        placeholder="John Doe"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">{t('contact.email_address')} *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        placeholder="john@company.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="company">{t('contact.company_name')}</Label>
                      <Input
                        id="company"
                        type="text"
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        placeholder="Acme Corp"
                        data-testid="contact-company-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="subject">{t('contact.subject')} *</Label>
                      <Select
                        value={formData.subject}
                        onValueChange={(value) => setFormData({ ...formData, subject: value })}
                        required
                      >
                        <SelectTrigger data-testid="contact-subject-select">
                          <SelectValue placeholder={t('contact.select_topic')} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">{t('contact.subjects.general')}</SelectItem>
                          <SelectItem value="sales">{t('contact.subjects.sales')}</SelectItem>
                          <SelectItem value="support">{t('contact.subjects.support')}</SelectItem>
                          <SelectItem value="partnership">{t('contact.subjects.partnership')}</SelectItem>
                          <SelectItem value="hosting">{t('contact.subjects.hosting')}</SelectItem>
                          <SelectItem value="exhibitor">{t('contact.subjects.exhibitor')}</SelectItem>
                          <SelectItem value="feedback">{t('contact.subjects.feedback')}</SelectItem>
                          <SelectItem value="other">{t('contact.subjects.other')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message">{t('contact.message')} *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      placeholder={t('contact.message_placeholder')}
                      rows={6}
                      data-testid="contact-message-input"
                    />
                  </div>

                  <Button type="submit" size="lg" disabled={loading} className="w-full sm:w-auto" data-testid="contact-submit-btn">
                    {loading ? (
                      t('contact.sending')
                    ) : (
                      <>
                        <Send className="mr-2 h-4 w-4" /> {t('contact.send')}
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">{t('contact.faq_title')}</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="p-6 bg-slate-50 rounded-lg">
              <h3 className="font-semibold text-slate-900 mb-2">{t('contact.faq_1_q')}</h3>
              <p className="text-slate-600 text-sm">
                {t('contact.faq_1_a')}
              </p>
            </div>
            <div className="p-6 bg-slate-50 rounded-lg">
              <h3 className="font-semibold text-slate-900 mb-2">{t('contact.faq_2_q')}</h3>
              <p className="text-slate-600 text-sm">
                {t('contact.faq_2_a')}
              </p>
            </div>
            <div className="p-6 bg-slate-50 rounded-lg">
              <h3 className="font-semibold text-slate-900 mb-2">{t('contact.faq_3_q')}</h3>
              <p className="text-slate-600 text-sm">
                {t('contact.faq_3_a')}
              </p>
            </div>
            <div className="p-6 bg-slate-50 rounded-lg">
              <h3 className="font-semibold text-slate-900 mb-2">{t('contact.faq_4_q')}</h3>
              <p className="text-slate-600 text-sm">
                {t('contact.faq_4_a')}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
